// Shared store-connect success ceremony (GLOBAL UX = shared component rule).
//
// Rendered after EVERY marketplace OAuth connect return (Etsy / Shopify /
// Amazon / eBay `?<provider>=connected`). Replaces the former etsy-era
// StoreConnectionSuccessModal: same celebration illustration + primary
// "List your first product" flow, plus the provider's mark and a short
// provider-aware next-steps list (the "Publish to eBay" step is eBay-only).
//
// Single close X rule: the shared DialogContent already renders a built-in
// close X — do NOT add another one here (PR#99 pattern).

import { PexIcon } from './icons/PexIcon'
import { Button } from './ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/dialog'
import { Dialog } from './ui/dialog.lib'
import { useT } from '../i18n'
import { storeCelebrationI18n } from '../i18n/shared/store-celebration'
import { ProviderMark } from '../features/seller/stores/ProviderMark'
import type { StoreProviderDefinition } from '../features/seller/stores/store-providers'
import highFiveIllustration from '../assets/illustrations/pexify-store-connected-high-five.png'

export function StoreConnectedCelebration({
  open,
  provider,
  storeName,
  onClose,
  onListFirstProduct,
}: {
  open: boolean
  // Provider definition of the store that just connected. Null-tolerant so an
  // unknown provider id still celebrates (generic title, no mark).
  provider: StoreProviderDefinition | null | undefined
  storeName?: string | null
  onClose: () => void
  onListFirstProduct: () => void
}) {
  const t = useT(storeCelebrationI18n)

  const steps: string[] = provider
    ? [
        t.steps.listingsMirror(provider.name),
        t.steps.orderIntake,
        ...(provider.id === 'ebay' ? [t.steps.publishEbay] : []),
        t.steps.manage,
      ]
    : [t.steps.orderIntake, t.steps.manage]

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <DialogContent className="max-w-xl gap-0 overflow-hidden rounded-2xl border-0 px-6 pb-7 pt-5 text-center shadow-2xl sm:px-10 sm:pb-9">
        <img
          src={highFiveIllustration}
          alt=""
          aria-hidden="true"
          className="mx-auto h-36 w-36 object-contain sm:h-44 sm:w-44"
        />
        <div className="mt-1 flex items-center justify-center gap-3">
          {provider ? <ProviderMark provider={provider} size="md" /> : null}
          <DialogTitle className="text-center text-2xl font-semibold tracking-tight">
            {provider ? t.title(provider.name) : t.genericTitle}
          </DialogTitle>
        </div>
        <p className="mt-3 text-sm font-medium text-slate-700">
          {storeName || t.fallbackStore}
          {provider ? (
            <>
              {' '}
              <span className="text-slate-300">·</span> {provider.name}
            </>
          ) : null}
        </p>
        <DialogDescription asChild>
          <div className="mx-auto mt-5 max-w-md text-left">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t.nextStepsHeading}
            </p>
            <ul className="mt-2 space-y-2">
              {steps.map((step) => (
                <li key={step} className="flex items-start gap-2 text-sm leading-6">
                  <PexIcon name="success" className="mt-1 text-emerald-600" size="xs" tone="inherit" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogDescription>
        <div className="mt-6 flex flex-col items-center gap-2">
          <Button
            variant="primary"
            className="min-w-56 rounded-full"
            onClick={onListFirstProduct}
          >
            {t.actions.listFirstProduct}
          </Button>
          <Button variant="ghost" className="min-w-40" onClick={onClose}>
            {t.actions.skip}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
